import React, { useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { DEFAULT_TIMEZONE } from 'Consts/timezone';
import {
  clearCreateCourseResult,
  createCourse,
  useSelectCreateCourseResult,
  useSelectIsCourseCreating,
  useSelectIsCreateCourseSuccess
} from 'Courses/data';
import { useAppDispatch } from 'Store';

import { useDateTimeConfigFromConfigSlice } from '@hooks/useDateTimeConfigFromConfigSlice';
import type { ICourseDates, ICreateCourseRequest } from '@libs/models';

import { convertCourseDatesToUtcFormat } from '../../dates/course-dates-utils';

import type { CourseFormData } from '../shared/types';

import { CreateCourseModal } from './create-course-modal';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const CreateCourseModalContainer = ({ open, onClose }: Readonly<Props>) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { dateFormat, timeFormat } = useDateTimeConfigFromConfigSlice();
  const lastRequestRef = useRef<ICreateCourseRequest | null>(null);

  const isCreateCourseLoading = useSelectIsCourseCreating();
  const isCreateCourseSuccess = useSelectIsCreateCourseSuccess();
  const createCourseResult = useSelectCreateCourseResult();

  const handleSubmit = (submissionData: CourseFormData) => {
    try {
      const request: ICreateCourseRequest = {
        title: submissionData.title,
        description: submissionData.description ?? '',
        timezone: submissionData.timezone ?? DEFAULT_TIMEZONE,
        maxParticipants: submissionData.maxParticipants ?? 20,
        expectedParticipants: submissionData.expectedParticipants ?? 10,
        daDatacenterIds: submissionData.daDataCenterIds ?? [],
        daOrganizationId: submissionData.daOrganizationId ?? 0,
        wantToBeCoordinator: submissionData.wantToBeCoordinator ?? true,
        isDemo: submissionData.isDemo ?? false,
        wantToBeTrainer: submissionData.wantToBeTrainer ?? false,
        courseDates: []
      };

      if (submissionData.courseDates && submissionData.courseDates.length > 0) {
        const utcCourseDates = convertCourseDatesToUtcFormat(
          submissionData.courseDates,
          submissionData.timezone,
          dateFormat,
          timeFormat
        );
        request.courseDates = utcCourseDates.map(
          dateEntry =>
            ({
              startDate: dateEntry.startDate,
              endDate: dateEntry.endDate
            }) as ICourseDates
        );
      }

      if (request.courseDates.length === 0) {
        const now = new Date();
        const futureDate = new Date(now.getTime() + 8 * 60 * 60 * 1000);
        request.courseDates = [
          {
            startDate: now.toISOString(),
            endDate: futureDate.toISOString()
          } as ICourseDates
        ];
      }

      lastRequestRef.current = request;

      dispatch(createCourse(request));
    } catch (error) {
      console.error('Error preparing course creation request:', error);
    }
  };

  const handleClose = () => {
    if (isCreateCourseSuccess) {
      dispatch(clearCreateCourseResult());
    }

    onClose();
  };

  useEffect(() => {
    if (isCreateCourseSuccess && createCourseResult && lastRequestRef.current) {
      try {
        toast.success(t('courseForm.courseCreatedSuccessfully'));

        onClose();
      } catch (error) {
        console.error('Error during course creation success handling:', error);
      }

      const timeout = window.setTimeout(() => {
        dispatch(clearCreateCourseResult());
        lastRequestRef.current = null;
      }, 1000);

      return () => {
        window.clearTimeout(timeout);
      };
    }
  }, [isCreateCourseSuccess, createCourseResult, onClose, dispatch, t]);

  return (
    <CreateCourseModal
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      isLoading={isCreateCourseLoading}
      isSuccess={isCreateCourseSuccess}
    />
  );
};
